// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Button, { ButtonPresentationProps } from "@awc/MobiusOverrides/Button/Button";
import { mergeWithCss } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, PropsWithChildren, useState } from "react";

export interface TitleTypographyProps {
    link?: string | LinkFieldValue;
    onLinkClick?: () => void;
    linkText?: string;
    openInNewTab?: boolean;
    extendedStyles?: TitleTypographyStyles;
    extraChild?: React.ReactNode;
    displayAsButton?: boolean;
}

type Props = TitleTypographyProps;

interface TitleTypographyLink {
    link?: LinkFieldValue;
    onClick?: () => void;
    url?: string;
    openInNewTab?: boolean;
    linkText?: string;
    styles: TitleTypographyStyles;
    displayAsButton?: boolean;
}

export interface TitleTypographyStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    link?: LinkPresentationProps;
    buttonStyles?: ButtonPresentationProps;
}

export const titleTypographyStyles: TitleTypographyStyles = {};

const TitleLink: FC<TitleTypographyLink> = ({ url, openInNewTab, linkText, styles, onClick, displayAsButton }) => {
    const history = useHistory();

    if (displayAsButton) {
        return (
            <Button
                onClick={(e: React.MouseEvent<Element, MouseEvent>) => {
                    e.stopPropagation();
                    if (url) {
                        if (url.startsWith("http")) {
                            window.location.href = url;
                        } else {
                            history.push(url);
                        }
                    }
                    onClick?.();
                }}
                variant="primary"
                sizeVariant="small"
                aria-label={linkText}
                {...styles.buttonStyles}
            >
                {linkText}
            </Button>
        );
    }
    return (
        <Link
            href={url}
            onClick={(e: React.MouseEvent<Element, MouseEvent>) => {
                e.stopPropagation();
                onClick?.();
            }}
            target={openInNewTab ? "_blank" : undefined}
            {...styles.link}
            aria-label={linkText}
        >
            {linkText}
        </Link>
    );
};

const TitleTypographyLink: FC<TitleTypographyLink> = ({ link, ...otherProps }) => {
    const { url } = useGetLink(link ?? { type: "Page", value: "" });
    return <TitleLink {...otherProps} url={url} />;
};

const TitleTypography: FC<PropsWithChildren<Props>> = ({
    openInNewTab,
    linkText,
    extendedStyles,
    children,
    link,
    displayAsButton = false,
    onLinkClick,
    extraChild,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeWithCss(titleTypographyStyles, extendedStyles));
    const linkProps = {
        styles,
        openInNewTab,
        linkText,
    };

    const linkComponent = () => {
        if ((!link && !onLinkClick) || !linkText) {
            return null;
        }
        if (typeof link === "string") {
            return <TitleLink aria-label={linkText} url={link} displayAsButton={displayAsButton} {...linkProps} />;
        }
        if (link && link.value) {
            return <TitleTypographyLink aria-label={linkText} link={link} {...linkProps} />;
        }
        if (onLinkClick) {
            return <TitleLink aria-label={linkText} onClick={onLinkClick} {...linkProps} />;
        }
        return null;
    };

    const linkElement = linkComponent();
    if (!React.Children.count(children) && !linkElement) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} {...otherProps}>
            <Typography {...styles.title}>{children}</Typography>
            {linkElement}
            {extraChild}
        </StyledWrapper>
    );
};

export default TitleTypography;
